// web/src/store/phone/_model.ts —— 内部：PhoneFields / INITIAL / PhoneStore 形状
// 拆自 web/src/store/phone.ts 的同名分区（内容原样搬运）；由 web/src/store/phone.ts 以 export * 聚合，导出面对调用方不变。

import { listCustomers, type Customer } from '../../api/crm';

import type { PhoneStatus } from './types';


interface PhoneFields {
  status: PhoneStatus;
  remoteNumber: string;
  direction: 'in' | 'out' | '';
  muted: boolean;
  held: boolean;
  startedAt?: number;
  /** 当前注册分机（未注册为空） */
  extension?: string;
  /** 来电/外呼号码 CRM 命中客户（挂断后保留，供话后处理/建工单；下一通电话覆盖） */
  customer: Customer | null;
  /**
   * 本通话的客户号码（后端 `/customers/by-call` 按「核验通过号 > 本通已收号手机号 >
   * 呼叫方向号 > 软电话对端号」解析出的明文号码；空=尚未解析）。
   *
   * 与 `remoteNumber` 分开：对端号只是「这通电话从哪个号打进来」，客户当场报号（坐席「收号管理」
   * 采集到手机号）或身份核验通过后，真正的客户是另一个号——客户运营区画像、服务记录都按本字段取数
   * （取数见 pages/workbench/ops/useOpsProfile，重解析触发见 pages/workbench/index.tsx）。
   * 挂断后保留（同 customer），下一通电话振铃/接通时复位。
   */
  customerPhone: string;
  /**
   * 电话条展示用客户名：外部业务档案（如 BOSS）优先、CRM 客户名兜底。
   * 与 CRM `customer.name` 分开——客户搜索/工单仍以 CRM 为准，这里只服务「来电显示叫什么」。
   */
  displayName: string;
  /**
   * 服务小结定位基线：本通电话响铃时本人最新一条 CDR id（无则 0）。
   * 挂断后 SummaryModal 只接受 id 更大的 CDR——CDR 由 ESL hangup 事件异步落库
   * （实测滞后 BYE ~300ms），弹窗首次查询必然先于落库，若无基线会把小结挂到上一通旧通话。
   * 挂断后保留（同 customer），下一通电话响铃时刷新。
   */
  cdrBaseline?: number;
  /**
   * 盲转完成标记（UI 语义）：refer() 成功后置位——本腿随后结束不属于「完整通话」，
   * 客户腿已由 FreeSWITCH 转往目标坐席继续服务，后端转出分支也不预写本腿 CDR。
   * 消费方（BasicLayout 小结触发 / 顶栏话后快照）见标记即跳过；新电话振铃/注册/手动挂断复位。
   */
  transferredOut?: boolean;
  /**
   * 外呼自动补 0 回显：系统/对端自动挂断外地号（is_local=false）后，待回显到顶栏号码框的
   * 补 0 号（如 "0138..."），供坐席免重输直接再次呼出。坐席主动挂断不触发；号码框消费后清空。
   */
  outboundEcho?: string;
  /**
   * 咨询转阶段：空=无第二路；ringing=专家振铃；up=专家已通、尚未完成转接。
   * 未上三方升会前接通后只能完成转接或取消，不要把客户腿当新外呼清掉。
   */
  consultPhase: '' | 'ringing' | 'up';
  /** 咨询目标展示名（工号/分机/技能组），仅条上提示用 */
  consultDest: string;
  /** 运行期引擎；咨询转只在 zitch 提供 */
  engine: 'freeswitch' | 'zitch' | '';
  /** 忙时同事邀请（kind=invite）；禁止当新客户弹屏；note 为邀请附言（可空） */
  invite: { call: number; from: string; note?: string } | null;
  /**
   * 已接听邀请的来源（含 call 号）：客户腿 ended 会走会话复位，条上号码靠本字段回填。
   * 邀请 ended 按 call 号清掉，不依赖卡片还在。
   */
  inviteTakeover: { call: number; from: string } | null;
}

export const INITIAL: PhoneFields = {
  status: 'idle',
  remoteNumber: '',
  direction: '',
  muted: false,
  held: false,
  startedAt: undefined,
  extension: undefined,
  customer: null,
  customerPhone: '',
  displayName: '',
  consultPhase: '',
  consultDest: '',
  engine: '',
  invite: null,
  inviteTakeover: null,
};

export interface PhoneStore extends PhoneFields {
  /**
   * 注册/重注册 SIP（幂等：相同分机且 UA 存活时直接返回；空参 = 注销）。
   * 由 BasicLayout 在坐席档案加载后调用，离开工作台页面不断线。
   * opts.silent：自愈静默重试（失败不弹错误提示，避免 FS 停机期刷屏；状态照常呈现）。
   */
  register: (extension?: string, password?: string, opts?: { silent?: boolean }) => Promise<void>;
  /** 注销并断开 UA（登出/切换账号时调用） */
  unregister: () => Promise<void>;
  /**
   * 软电话自愈兜底（心跳/页面回前台时调用）：坐席在线但软电话不在注册态
   * （注册失败/注册丢失/引擎 error），或 WS 曾断线未恢复（注册可能已陈旧）时，
   * 按冷却静默重试注册，避免「系统在线但电话不可达」持续。
   */
  healIfNeeded: () => Promise<void>;
  answer: () => Promise<void>;
  hangup: () => Promise<void>;
  call: (target: string) => Promise<void>;
  sendDtmf: (tone: string) => void;
  toggleMute: () => void;
  toggleHold: () => Promise<void>;
  /** 盲转：SIP REFER 到目标分机/号码 */
  refer: (target: string) => Promise<void>;
  /** 咨询邀请：先 hold 客户，再拨专家第二路（仅 zitch） */
  inviteExpert: (dest: string, label?: string) => Promise<void>;
  /** 完成咨询转：自己退出，客户与专家继续通话 */
  completeConsultTransfer: () => Promise<void>;
  /** 取消咨询：挂专家 + unhold 客户 */
  cancelConsult: () => Promise<void>;
  /** 忽略忙时邀请（只挂邀请腿） */
  dismissInvite: () => void;
  /** 接听忙时邀请：hold=保持当前 / hangup=挂断当前 / answer=无当前通话时直接接 */
  acceptInvite: (mode: 'hold' | 'hangup' | 'answer') => Promise<void>;
  /**
   * 重新解析本通话的客户并刷新弹屏数据：给 uuid 时走通话语境匹配端点（后端按核验号/已收号手机号/
   * 呼叫方向号/对端号选号），不给则退回按当前对端号码匹配 CRM（一键新建客户后刷新弹屏数据）。
   */
  refreshCustomer: (uuid?: string) => Promise<void>;
  /** 清除外呼补 0 回显（号码输入框消费后调用） */
  clearOutboundEcho: () => void;
}
